import {
  conditionCureDefaults,
  conditionTypeDefaults,
  conditionsDefaults,
} from '../../lib/teamsDefaults';
import {
  CONDITION_IDS,
  type ConditionCureDef,
  type ConditionDef,
  type ConditionsDef,
  issuesUnder,
} from '../../lib/validate';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  ItemPicker,
  ListEditor,
  NumberField,
  SelectField,
  SelectInput,
  TextField,
} from './fields';
import { useContentRefs } from './refs';
import { StatMapEditor } from './hb/StatsForms';
import type { FormProps } from './types';
import { TierSelectField } from './wind/WindForms';
import { conditionTypeWindDefaults } from '../../lib/wind/defaults';

type NumOpts = { integer?: boolean; min?: number; max?: number; step?: number; hint?: string };
const share: NumOpts = { max: 1, step: 0.05, hint: '0..1' };
const mult: NumOpts = { max: 1, step: 0.05, hint: 'Above 0, at most 1.' };

const CONDITION_LABELS: Record<(typeof CONDITION_IDS)[number], string> = {
  wounded: 'Wounded',
  laid_up: 'Laid up',
  sick: 'Sick',
  bitten: 'Bitten',
};

/**
 * Survivor conditions (ADR 0031): the on/off switch, the cause thresholds and duration cuts, and the four condition types with their
 * effects and cures. Absent fields show the C# defaults (`conditionsDefaults`), like on the server.
 */
export function ConditionsForm({ value, onChange, errors }: FormProps<ConditionsDef>) {
  const c: ConditionsDef = { ...conditionsDefaults(), ...value };
  const set = <K extends keyof ConditionsDef>(k: K, v: ConditionsDef[K]) =>
    onChange({ ...c, [k]: v });
  const num = (k: keyof ConditionsDef & string, label: string, o: NumOpts = {}) => (
    <NumberField
      key={k}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={o.min ?? 0}
      max={o.max}
      step={o.step}
      value={c[k] as number | undefined}
      issues={errors[k]}
      onChange={(v) => set(k, (v ?? 0) as never)}
    />
  );
  const types = (c.types ?? []).map((t) => t ?? conditionTypeDefaults());

  return (
    <>
      <div
        className={`mb-4 flex items-center justify-between rounded border-2 p-3 ${
          c.enabled ? 'border-benday bg-benday/10' : 'border-blam bg-blam/10'
        }`}
      >
        <div>
          <p className="text-sm font-bold uppercase tracking-wide">
            Injuries and sickness are {c.enabled ? 'ON' : 'OFF'}
          </p>
          <p className="text-xs text-bruise">
            Off: nothing new is caused and no condition has an effect. Publish to apply; no app
            release needed.
          </p>
        </div>
        <button
          type="button"
          className={`rounded border-2 px-3 py-1 text-sm font-bold ${
            c.enabled ? 'border-blam text-blam' : 'border-benday text-benday'
          }`}
          onClick={() => set('enabled', !c.enabled)}
        >
          {c.enabled ? 'Turn off' : 'Turn on'}
        </button>
      </div>

      <Fieldset legend="Recovery cuts">
        {num('infirmaryCutPerLevel', 'Infirmary cut per level', {
          ...share,
          hint: 'Per Infirmary level, while the survivor is at home.',
        })}
        {num('minDurationMult', 'Shortest duration', {
          ...mult,
          hint: 'The cuts never go below this share.',
        })}
      </Fieldset>

      <Fieldset legend="Causes">
        {num('woundedBelowHealthFrac', 'Wounded below health share', share)}
        {num('laidUpBelowHealthFrac', 'Laid up below health share', {
          ...share,
          hint: '0..1, at most the wounded share. The player is wounded instead.',
        })}
        {num('starvingDaysToLayUp', 'Starving days to lay up', {
          integer: true,
          hint: '0 = never.',
        })}
        {num('shortageSickChance', 'Sick chance on a shortage day', share)}
        {num('riskySickChance', 'Sick chance per risky unit eaten', share)}
        {num('biteChancePer10Damage', 'Bite chance per 10 damage (old)', {
          ...share,
          step: 0.01,
          hint: 'Old, kept one content version: bite chance per grab replaces it.',
        })}
        {num('maxBiteChance', 'Max bite chance', share)}
      </Fieldset>

      <Fieldset legend="Causes (WIND)">
        <TierSelectField
          label="Wounded at tier"
          hint="A raid that ends at this tier or deeper..."
          value={c.woundedAtTier}
          issues={errors.woundedAtTier}
          onChange={(v) => set('woundedAtTier', v)}
        />
        {num('woundedAtWounds', '...or with this many wounds', {
          integer: true,
          hint: 'Wounds in the raid that make a survivor Wounded.',
        })}
        <TierSelectField
          label="Laid up at tier"
          hint="Laid up needs both this tier..."
          value={c.laidUpAtTier}
          issues={errors.laidUpAtTier}
          onChange={(v) => set('laidUpAtTier', v)}
        />
        {num('laidUpAtWounds', '...and this many wounds', { integer: true })}
        {num('biteChancePerGrab', 'Bite chance per grab', {
          ...share,
          step: 0.01,
          hint: '0..1. Up to the max bite chance.',
        })}
        <TierSelectField
          label="Player turned max tier"
          hint="The player never turns: after the bite timer their best tier is this one until cured."
          value={c.playerTurnedMaxTier}
          issues={errors.playerTurnedMaxTier}
          onChange={(v) => set('playerTurnedMaxTier', v)}
        />
        {num('minWindCapacityMult', 'Lowest wind tank multiplier', {
          min: 0.1,
          max: 1,
          step: 0.05,
          hint: '0.1..1. The conditions together never shrink the tank below this.',
        })}
      </Fieldset>

      <Fieldset legend="Max HP (old, kept one content version)">
        {num('minMaxHealthMult', 'Lowest max HP multiplier (old)', mult)}
        {num('playerTurnedMaxHealthMult', "Player's max HP after the bite timer (old)", {
          ...mult,
          hint: 'Old: player turned max tier replaces it.',
        })}
      </Fieldset>

      <ListEditor<ConditionDef>
        label="Condition types"
        what="condition"
        items={types}
        make={conditionTypeDefaults}
        onChange={(next) => set('types', next)}
        issues={errors.types}
        row={(t, j, setT) => (
          <ConditionTypeRow
            value={{ ...conditionTypeDefaults(), ...t }}
            onChange={setT}
            path={`types.${j}`}
            allErrors={errors}
          />
        )}
      />
    </>
  );
}

function ConditionTypeRow({
  value: t,
  onChange,
  path,
  allErrors,
}: {
  value: ConditionDef;
  onChange: (v: ConditionDef) => void;
  path: string;
  allErrors: FormProps<ConditionsDef>['errors'];
}) {
  const { baseModules } = useContentRefs();
  const set = <K extends keyof ConditionDef>(k: K, v: ConditionDef[K]) =>
    onChange({ ...t, [k]: v });
  const e = (field: string) => allErrors[`${path}.${field}`];
  const num = (k: keyof ConditionDef & string, label: string, o: NumOpts = {}) => (
    <NumberField
      key={k}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={o.min ?? 0}
      max={o.max}
      step={o.step}
      value={t[k] as number | undefined}
      issues={e(k)}
      onChange={(v) => set(k, (v ?? 0) as never)}
    />
  );
  const cures = (t.cures ?? []).map((x) => x ?? conditionCureDefaults());
  const moduleOptions = baseModules.map((m) => ({
    value: m.id ?? '',
    label: m.name || m.id || '',
  }));

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <SelectField
        label="Kind"
        value={t.id}
        options={CONDITION_IDS.map((id) => ({
          value: id,
          label: `${CONDITION_LABELS[id]} (${id})`,
        }))}
        issues={e('id')}
        onChange={(v) => set('id', v ?? '')}
      />
      <TextField label="Name" value={t.name} issues={e('name')} onChange={(v) => set('name', v)} />
      <StatMapEditor
        label="Stat penalties"
        hint="Points added to the effective stat while active (e.g. wounded AIM -1). Outside the effects' cap. Blank = none."
        value={t.statMods}
        issues={e('statMods')}
        onChange={(m) => set('statMods', m)}
      />
      <TextField
        label="Description"
        multiline
        value={t.description}
        onChange={(v) => set('description', v)}
      />
      {num('severity', 'Severity', {
        integer: true,
        hint: 'Higher = worse; the badge shows the worst.',
      })}
      {num('homeHours', 'Hours to recover at home', { step: 1, hint: '0 = never by itself.' })}
      {num('awayHours', 'Hours to recover away', { step: 1, hint: '0 = never by itself.' })}
      {num('windCapacityMult', 'Wind tank multiplier', {
        min: 0.1,
        max: 1,
        step: 0.05,
        hint: `0.1..1 while active (seed ${conditionTypeWindDefaults(t.id).windCapacityMult}).`,
      })}
      {num('windRecoveryMult', 'Wind recovery multiplier', {
        min: 0.1,
        max: 1.5,
        step: 0.05,
        hint: `0.1..1.5 while active (seed ${conditionTypeWindDefaults(t.id).windRecoveryMult}).`,
      })}
      {num('maxHealthMult', 'Max HP multiplier (old)', {
        ...mult,
        hint: 'Old, kept one content version: the wind tank multiplier replaces it.',
      })}
      {num('sprintMult', 'Sprint multiplier (leader)', mult)}
      {num('moveNoiseMult', 'Footstep noise multiplier (team)', {
        min: 1,
        max: 3,
        step: 0.05,
        hint: '1..3',
      })}
      {num('regenMult', 'Team energy regen multiplier', share)}
      {num('turnHours', 'Hours until a recruit turns', {
        step: 1,
        hint: '0 = never. Not cut by medics.',
      })}
      <CheckboxField
        label="Sits out raids"
        hint="Still rides with the team. Never applies to the player."
        checked={!!t.sitsOut}
        onChange={(v) => set('sitsOut', v)}
      />
      <ListEditor<ConditionCureDef>
        label="Cures"
        what="cure"
        hint="Any one of these items cures it at once."
        items={cures}
        make={conditionCureDefaults}
        issues={e('cures')}
        onChange={(next) => set('cures', next)}
        row={(cure, k, setCure) => (
          <div className="grid gap-1 sm:grid-cols-3">
            <ItemPicker
              label={`Cure ${k + 1} item`}
              value={cure.itemId}
              invalid={!!e(`cures.${k}.itemId`)}
              onChange={(v) => setCure({ ...cure, itemId: v ?? '' })}
            />
            <SelectInput
              label={`Cure ${k + 1} module`}
              value={cure.moduleId}
              none="(no module)"
              options={moduleOptions}
              invalid={!!e(`cures.${k}.moduleId`)}
              onChange={(v) =>
                setCure({
                  ...cure,
                  moduleId: v,
                  moduleLevel: v ? Math.max(1, cure.moduleLevel ?? 0) : 0,
                })
              }
            />
            <NumberField
              label="Module level"
              integer
              min={0}
              value={cure.moduleLevel}
              issues={e(`cures.${k}.moduleLevel`)}
              onChange={(v) => setCure({ ...cure, moduleLevel: v ?? 0 })}
            />
            <div className="sm:col-span-3">
              <IssueList issues={issuesUnder(allErrors, `${path}.cures.${k}`)} />
            </div>
          </div>
        )}
      />
    </div>
  );
}
